import Loader from 'Components/Common/Loader';
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { createCampaignFromIdea, fetchCampaignBrief, fetchCampaignBriefIdeas, fetchCampaignIdeas, fetchCampaignStrategies, generateCampaignIdeas, generateCampaignIdeasFromBrief } from 'services/api/campaign';
import { AppDispatch, RootState } from 'store';
import { CampaignBrief, CampaignIdea, CampaignStrategy } from 'types/campaign';
import { getErrorMessage } from 'utils/helpers';
import '../../../pages/Campaigns/style.scss';
import { Link, useNavigate } from 'react-router-dom';
import { UserBrand } from 'types/users';
import { fetchUserBrands as fetchUserBrandsThunk } from 'slices/users/brands/thunk';
import AICampaignCreationLayout from 'Layouts/AICampaignCreationLayout';
import AICampaignIdeas from './AICampaignIdeas';

const POLL_INTERVAL = 10000;

const CampaignCreationAIBrief: React.FC<{ brandSocialAccountId: string | number, onBrandChange?: (brand: UserBrand | null) => void }> = ({ brandSocialAccountId, onBrandChange }) => {
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const dispatch: AppDispatch = useDispatch();
    const [campaignStrategies, setCampaignStrategies] = useState<CampaignStrategy[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedStrategyUuid, setSelectedStrategyUuid] = useState<string>('');
    const userBrands = useSelector((state: RootState) => state.userBrands.items);
    const [brandMenuOpen, setBrandMenuOpen] = useState(false);
    const brandMenuRef = React.useRef<HTMLDivElement | null>(null);
    const [generatingIdeas, setGeneratingIdeas] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [ideasGeneratedAt, setIdeasGeneratedAt] = useState<string | null>(null);
    const [newIdeasAvailable, setNewIdeasAvailable] = useState(false);
    const [selectedIdea, setSelectedIdea] = useState<CampaignIdea | null>(null);
    const [fetchingIdeas, setFetchingIdeas] = useState(false);

    const [campaignIdeas, setCampaignIdeas] = useState<CampaignIdea[]>([]);
    const [creatingCampaign, setCreatingCampaign] = useState(false);
    const navigate = useNavigate();
    const [notes, setNotes] = useState<string>('');

    const [brief, setBrief] = useState<CampaignBrief | null>(null);

    useEffect(() => {
        dispatch(fetchUserBrandsThunk(false));
    }, [dispatch]);

    useEffect(() => {
        if (!brandMenuOpen) return;
        const onDocMouseDown = (e: MouseEvent) => {
            const el = brandMenuRef.current;
            if (!el) return;
            if (e.target instanceof Node && el.contains(e.target)) return;
            setBrandMenuOpen(false);
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [brandMenuOpen]);


    const createCampaign = async () => {
        if (!selectedIdea || !selectedAgencyId) return;
        setCreatingCampaign(true);
        try {
            const response = await createCampaignFromIdea(selectedAgencyId, selectedIdea.uuid);
            if (response.campaign?.uuid) {
                navigate(`/campaign/${response.campaign.uuid}/edit`);
            }

        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to create campaign'));
        } finally {
            setCreatingCampaign(false);
        }

    }

    const fetchIdeas = async () => {
        if (!selectedAgencyId || !brandSocialAccountId || !brief) return;
        setFetchingIdeas(true);
        try {
            const response = await fetchCampaignBriefIdeas(selectedAgencyId, brandSocialAccountId, brief.uuid);
            setCampaignIdeas(response?.items ?? []);
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to fetch campaign ideas'));
        } finally {
            setFetchingIdeas(false);
        }
    }

    const fetchBrief = async () => {
        if (!selectedAgencyId || !brandSocialAccountId || !brief?.uuid) return;
        try {
            const updatedBrief = await fetchCampaignBrief(selectedAgencyId, brandSocialAccountId, brief.uuid);
            if (updatedBrief && brief?.uuid === updatedBrief.uuid) {
                // console.log('updatedBrief', updatedBrief);
                // console.log('brief', brief);
                setBrief(updatedBrief);
                // console.log('new Date(updatedBrief.ideas_generated_at ?? 0).getTime()', new Date(updatedBrief.ideas_generated_at ?? 0).getTime());
                // console.log('new Date(brief.ideas_generated_at ?? 0).getTime()', new Date(brief.ideas_generated_at ?? 0).getTime());
                // console.log('new Date(updatedBrief.ideas_generated_at ?? 0).getTime() > new Date(brief.ideas_generated_at ?? 0).getTime()', new Date(updatedBrief.ideas_generated_at ?? 0).getTime() > new Date(brief.ideas_generated_at ?? 0).getTime());
                if ((new Date(updatedBrief.ideas_generated_at ?? 0).getTime()) > new Date(brief.ideas_generated_at ?? 0).getTime()) {
                    setNewIdeasAvailable(true);
                    setGeneratingIdeas(false);
                    // console.log('new ideas available');
                    // console.log('fetching ideas');
                    fetchIdeas();
                } else {
                    // console.log('no new ideas available');
                    setTimeout(() => { fetchBrief() }, POLL_INTERVAL);
                }
            }
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to fetch campaign brief'));
        }
    }

    useEffect(() => {
        if (generatingIdeas && brandSocialAccountId && selectedAgencyId) {
            setTimeout(() => { fetchBrief() }, POLL_INTERVAL);
        }
    }, [generatingIdeas, brandSocialAccountId, selectedAgencyId])



    const generateIdeas = async () => {
        if (!notes || !selectedAgencyId || !brandSocialAccountId) return;
        setGenerating(true);
        try {
            const response = await generateCampaignIdeasFromBrief(selectedAgencyId, brandSocialAccountId, notes);
            setBrief(response?.brief ?? null);
            setGeneratingIdeas(true);
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to generate campaign ideas'));
            setGeneratingIdeas(false);
        } finally {
            setGenerating(false);
        }

    }

    if (generatingIdeas && !newIdeasAvailable) {
        return (
            <div className="campaign-ai-strategy d-flex flex-column align-items-center justify-content-center vh-50">
                <div className="loader mb-2"></div>
                <div className="mt-2 text-muted" style={{ fontFamily: 'Rubik, sans-serif' }}>
                    Generating Ideas...
                </div>
            </div>
        );
    }

    if (loading) return <Loader />

    return (
        <AICampaignCreationLayout
            steps={[
                {
                    label: 'Brief Campaign',
                    title: 'Brief your campaign',
                    subtitle: 'Brief your campaign to get started.',
                    active: !newIdeasAvailable,
                    completed: newIdeasAvailable,
                },
                {
                    label: 'Choose Campaign',
                    title: 'Choose a Campaign',
                    subtitle: 'Each campaign is a ready-to-deploy content brief aligned with this strategy.',
                    active: newIdeasAvailable,
                    completed: false,
                },
                {
                    label: 'Review',
                    title: 'Review',
                    subtitle: 'Review the campaign and make any final adjustments.',
                    active: false,
                    completed: false,
                }
            ]}
            brandSocialAccountId={brandSocialAccountId}
            onBrandChange={onBrandChange}
            bottomBarContent={
                newIdeasAvailable ? (
                    <button
                        type="button"
                        className="btn btn-gradient"
                        disabled={!selectedIdea || creatingCampaign}
                        onClick={createCampaign}
                    >
                        {creatingCampaign ? 'CREATING CAMPAIGN...' : 'USE THIS CAMPAIGN SETUP'}
                    </button>
                ) : (
                    <button
                        type="button"
                        className="btn btn-gradient"
                        disabled={!notes || generating}
                        onClick={generateIdeas}
                    >
                        {generating ? 'PLEASE WAIT...' : 'GENERATE IDEAS'}
                    </button>
                )
            }
        >
            {
                newIdeasAvailable ? (
                    <AICampaignIdeas loading={fetchingIdeas} campaignIdeas={campaignIdeas} onIdeaSelect={setSelectedIdea} />
                ) : (
                    <div className="campaign-ai-creation__content">
                        <textarea className="form-control campaign-ai-creation__textarea" placeholder="Enter your notes here" value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </div>
                )
            }

        </AICampaignCreationLayout>
    )
}

export default CampaignCreationAIBrief